import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  Flame,
  GraduationCap,
  Play,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import { API } from "../contexts/AuthContext";
import WhatsAppIcon from "../../components/icons/WhatsAppIcon";
import BatchCover, { batchHasCoverVideo } from "../../components/BatchCover";
import FeatureVideoModal from "../../components/FeatureVideoModal";
import "../lms.css";

/* ---------- Canonical landing-page feature list, reused when an admin batch
     doesn't supply its own `highlights[]`. Keeps the Explore modal looking
     complete with the marquee LW feature-set straight from the hero cards. */
const CANONICAL_LANDING_HIGHLIGHTS = [
  "Concept-first video lectures by top MDCAT instructors",
  "Per-lecture, per-chapter & full-book analytics",
  "Advanced MCQ engine with 5,000+ question bank",
  "Daily doubt-solving with expert teachers",
  "1:1 MBBS mentor circle — chat + audio calls",
  "Spaced-repetition flashcards & revision decks",
  "Private LW Community — peer learning feed",
  "Live leaderboards — subject, chapter & global",
  "Printable MDCAT notes for last-mile revision",
  "Anti-piracy signed video streaming (Bunny.net)",
];

/* ---------- Helpers ---------- */

/** Formats `923001234567` + batch object into a WA deep-link with pre-filled text. */
const buildWhatsAppLink = (number, batch) => {
  if (!number) return null;
  const digits = (number || "").toString().replace(/\D/g, "");
  if (!digits) return null;
  const price = batch?.price_one_time
    ? ` (One-time: Rs. ${Number(batch.price_one_time).toLocaleString()})`
    : "";
  const msg =
    `Hi LogicsWallah! 👋\n\nI'm interested in the *${batch?.name || "MDCAT batch"}*${price}. ` +
    `Can you share more details on schedule, faculty, and how I can enroll?\n\nThanks!`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
};

/** Compute a readable "status" chip for a batch. Server returns `status`; we fall back. */
const statusMeta = (batch) => {
  const s = batch?.status || (batch?.registration_open ? "open" : "closed");
  if (s === "open")
    return { label: "OPEN FOR REGISTRATION", cls: "border-[rgba(0,229,255,0.5)] bg-[rgba(0,229,255,0.12)] text-[#5ceeff]" };
  if (s === "coming_soon")
    return { label: "COMING SOON", cls: "border-[rgba(167,139,250,0.55)] bg-[rgba(167,139,250,0.14)] text-[#c4b5fd]" };
  if (s === "upcoming")
    return { label: "OPENING SOON", cls: "border-[#ffcb47]/50 bg-[#ffcb47]/10 text-[#ffcb47]" };
  if (s === "closed")
    return { label: "REGISTRATION CLOSED", cls: "border-rose-300/40 bg-rose-500/10 text-rose-200" };
  return { label: String(s).toUpperCase(), cls: "border-white/20 bg-white/5 text-white/70" };
};

/**
 * Distance-from-now in (days, hours, mins, secs). Null if no target or passed.
 */
function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  if (!Number.isFinite(target)) return null;
  const diff = target - now;
  if (diff <= 0) return null;
  const secs = Math.floor(diff / 1000);
  return {
    days: Math.floor(secs / 86400),
    hours: Math.floor((secs % 86400) / 3600),
    mins: Math.floor((secs % 3600) / 60),
    secs: secs % 60,
    totalSecs: secs,
  };
}

/**
 * CountdownStrip — cinematic 4-tile counter for "Opens in…" / "Closes in…".
 * Tone: "violet" (coming soon / opens), "amber" (closing urgency).
 */
function CountdownStrip({ targetIso, tone = "violet", label }) {
  const c = useCountdown(targetIso);
  if (!c) return null;
  const palette = tone === "amber"
    ? { bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.35)", fg: "#fcd34d", label: "#fde68a" }
    : { bg: "rgba(167,139,250,0.10)", border: "rgba(167,139,250,0.35)", fg: "#c4b5fd", label: "#ddd6fe" };
  const Tile = ({ v, k }) => (
    <div className="flex flex-col items-center min-w-[40px]">
      <div className="lms-display text-white text-lg md:text-xl leading-none tabular-nums"
           style={{ textShadow: `0 0 18px ${palette.bg}` }}>
        {String(v).padStart(2, "0")}
      </div>
      <div className="lms-mono text-[8px] mt-1 tracking-[0.2em]" style={{ color: palette.label }}>{k}</div>
    </div>
  );
  return (
    <div
      className="mt-3 rounded-[12px] px-3 py-2.5 flex items-center gap-3 backdrop-blur-sm"
      style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
      data-testid="batch-countdown"
    >
      <div className="flex-1 min-w-0">
        <div className="lms-mono text-[9px] uppercase tracking-[0.16em]" style={{ color: palette.label }}>
          {label}
        </div>
        <div className="lms-display text-white text-[13px] leading-tight mt-0.5 truncate">
          {c.days > 0 ? `${c.days} day${c.days === 1 ? "" : "s"}` : c.hours > 0 ? `${c.hours} hour${c.hours === 1 ? "" : "s"}` : `${c.mins}m ${c.secs}s`}
        </div>
      </div>
      <div className="flex items-end gap-1.5 shrink-0">
        <Tile v={c.days}  k="D" />
        <span style={{ color: palette.fg }} className="lms-display pb-3 leading-none text-sm">:</span>
        <Tile v={c.hours} k="H" />
        <span style={{ color: palette.fg }} className="lms-display pb-3 leading-none text-sm">:</span>
        <Tile v={c.mins}  k="M" />
        <span style={{ color: palette.fg }} className="lms-display pb-3 leading-none text-sm">:</span>
        <Tile v={c.secs}  k="S" />
      </div>
    </div>
  );
}

const niceDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

/* ---------- Page ---------- */

export default function ApplyPage() {
  const [batches, setBatches] = useState(null);
  const [siteConfig, setSiteConfig] = useState({ whatsapp_number: "" });
  const [err, setErr] = useState("");
  const [exploring, setExploring] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/batches/open-for-registration`)
      .then((r) => setBatches(Array.isArray(r.data) ? r.data.filter((b) => b.display_on_landing !== false) : []))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load batches. Please try again."));
    axios
      .get(`${API}/site/config`)
      .then((r) => setSiteConfig(r.data || {}))
      .catch(() => {});
  }, []);

  return (
    <div className="lms-root min-h-screen flex flex-col">
      <div aria-hidden className="fixed inset-0 lms-grain" />

      {/* Top bar */}
      <div className="relative z-10 px-6 md:px-10 pt-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition" data-testid="apply-back-home">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <span className="lms-mono">LogicsWallah · Apply</span>
      </div>

      <div className="relative z-10 flex-1 px-5 md:px-10 py-10 md:py-16 max-w-6xl w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <div className="lms-mono text-[#5ceeff] mb-3">— Apply for a batch</div>
          <h1 className="lms-display text-white text-4xl md:text-6xl leading-[1.03]">
            Pick your batch,
            <br />
            <em className="text-[#ffd970]" style={{ fontStyle: "italic" }}>
              and let's begin.
            </em>
          </h1>
          <p className="text-white/55 text-sm md:text-base mt-4 max-w-xl leading-relaxed">
            Every batch has a dedicated application form. We review each application personally and activate access
            within 24 hours of payment confirmation.
          </p>
        </motion.div>

        {err && (
          <div
            className="mt-8 p-4 rounded-[14px] border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm"
            data-testid="apply-error"
          >
            {err}
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="apply-batches-grid">
          {batches === null ? (
            [0, 1, 2, 3].map((i) => <div key={i} className="lms-shimmer h-[420px]" />)
          ) : batches.length === 0 ? (
            <div className="md:col-span-2 lms-glass-strong p-10 text-center" data-testid="apply-empty">
              <Sparkles size={22} className="mx-auto text-[#ffcb47] mb-3" />
              <div className="lms-display text-white text-xl">No batches are open for registration</div>
              <p className="text-white/55 text-sm mt-2 max-w-md mx-auto">
                Registrations aren't open at this moment. Please check back soon, or leave a callback request from the
                landing page.
              </p>
              <Link to="/" className="lms-btn lms-btn-ghost mt-5 inline-flex" data-testid="apply-back">
                ← Back to site
              </Link>
            </div>
          ) : (
            batches.map((b, i) => (
              <BatchCard
                key={b.id}
                batch={b}
                index={i}
                siteWhatsApp={siteConfig.whatsapp_number}
                onExplore={() => setExploring(b)}
              />
            ))
          )}
        </div>

        <div className="mt-12 text-center text-white/45 text-xs">
          Already a Logician?{" "}
          <Link to="/login" className="text-[#5ceeff] hover:underline" data-testid="apply-login-link">
            Sign in here
          </Link>
        </div>
      </div>

      <div
        aria-hidden
        className="fixed bottom-[-30%] left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.14), transparent 60%)", filter: "blur(60px)" }}
      />

      <AnimatePresence>
        {exploring && (
          <ExploreModal
            batch={exploring}
            siteWhatsApp={siteConfig.whatsapp_number}
            onClose={() => setExploring(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Single batch card ---------- */

function BatchCard({ batch, index, siteWhatsApp, onExplore }) {
  const meta = statusMeta(batch);
  const status = batch.status || (batch.registration_open ? "open" : "closed");
  // `upcoming` batches accept applications too (enrolment opens before classes start).
  // `coming_soon` batches do NOT accept applications — waitlist/WhatsApp only.
  const isOpen = status === "open" || status === "upcoming";
  const isComingSoon = status === "coming_soon";
  const waLink = buildWhatsAppLink(batch.whatsapp_number || siteWhatsApp, batch);
  const showDiscount =
    batch.discount_price_one_time && batch.price_one_time && batch.discount_price_one_time > batch.price_one_time;
  const discountPct = showDiscount
    ? Math.round(((batch.discount_price_one_time - batch.price_one_time) / batch.discount_price_one_time) * 100)
    : 0;

  const Icon =
    batch.type === "parallel" ? GraduationCap : batch.type === "repeaters" ? Target : Flame;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * index, ease: [0.2, 0.8, 0.2, 1] }}
      className="lms-card overflow-hidden flex flex-col group"
      data-testid={`apply-batch-${batch.slug}`}
    >
      {/* Cover — image (always full-bleed via object-cover, aspect matches the
          admin 1600×540 upload guide) or gradient fallback. */}
      <div
        className={`relative w-full ${batchHasCoverVideo(batch) ? "aspect-video" : "aspect-[1600/540]"} overflow-hidden`}
        style={{
          background:
            "linear-gradient(135deg, rgba(0,229,255,0.25), rgba(255,211,112,0.18) 60%, rgba(12,24,48,0.8))",
        }}
      >
        {batch.cover_image || batchHasCoverVideo(batch) ? (
          <BatchCover
            batch={batch}
            alt={batch.name}
            videoFit="cover"
            imgClassName="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="absolute inset-0 cinematic-dots-dense opacity-20" />
        )}
        {/* Gradient scrim for readability — pointer-events-none so it
            never blocks clicks on the BatchCover's play button. */}
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(6,18,38,0.95)] via-[rgba(6,18,38,0.35)] to-transparent pointer-events-none" />
        {/* Status badge */}
        <div className={`absolute top-3 left-3 lms-chip ${meta.cls}`} data-testid={`batch-card-status-${batch.slug}`}>
          {meta.label}
        </div>
        {/* Discount chip */}
        {showDiscount && (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] lms-mono">
            {discountPct}% OFF
          </div>
        )}
        {/* Icon pill + optional badge */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <div className="w-11 h-11 rounded-full bg-[rgba(6,18,38,0.85)] border border-white/15 flex items-center justify-center">
            <Icon size={18} className="text-[#5ceeff]" />
          </div>
          {batch.badge && (
            <span className="lms-chip border-[#ffcb47]/40 bg-[#ffcb47]/10 text-[#ffcb47]">{batch.badge}</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <h3 className="lms-display text-white text-xl md:text-[1.65rem] leading-[1.15] tracking-[-0.01em]">{batch.name}</h3>
        </div>
        {batch.tagline && (
          <p className="text-white/65 text-[13.5px] mt-2.5 leading-relaxed line-clamp-2">{batch.tagline}</p>
        )}

        {/* Meta strip — bullet-separated, mono, subtle */}
        {(batch.audience || batch.language || batch.duration) && (
          <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1.5 text-[10.5px] lms-mono text-white/55 uppercase tracking-[0.08em]">
            {batch.audience && <span>{batch.audience}</span>}
            {batch.audience && (batch.language || batch.duration) && <span className="text-white/25">·</span>}
            {batch.language && <span>{batch.language}</span>}
            {batch.language && batch.duration && <span className="text-white/25">·</span>}
            {batch.duration && <span>{batch.duration}</span>}
          </div>
        )}

        {(batch.start_date || batch.end_date) && (
          <div className="mt-3 flex items-center gap-2 text-[11px] text-white/55">
            <Calendar size={12} className="text-[#5ceeff]/70" />
            {batch.start_date && <span>Starts {niceDate(batch.start_date)}</span>}
            {batch.end_date && <span className="text-white/35">·</span>}
            {batch.end_date && <span>Ends {niceDate(batch.end_date)}</span>}
          </div>
        )}

        {/* Coming-soon teaser note (admin-written one-liner) */}
        {isComingSoon && batch.coming_soon_note && (
          <div
            className="mt-3 rounded-[10px] px-3 py-2 text-[12px] leading-relaxed italic"
            style={{
              background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(167,139,250,0.02))",
              border: "1px solid rgba(167,139,250,0.22)",
              color: "#ddd6fe",
            }}
            data-testid={`batch-coming-note-${batch.slug}`}
          >
            "{batch.coming_soon_note}"
          </div>
        )}

        {/* Registration window countdowns — one source, two contextual tones */}
        {(isComingSoon || status === "upcoming") && batch.registration_opens_at && (
          <CountdownStrip
            targetIso={batch.registration_opens_at}
            tone="violet"
            label="Registrations open in"
          />
        )}
        {status === "open" && batch.registration_closes_at && (
          <CountdownStrip
            targetIso={batch.registration_closes_at}
            tone="amber"
            label="Registrations close in"
          />
        )}

        {/* Price — bigger, tighter, refined */}
        <div className="mt-5 pt-5 border-t border-white/[0.07]">
          <div className="flex items-baseline gap-2.5 flex-wrap">
            {batch.price_one_time ? (
              <>
                <span className="lms-display text-white text-[1.7rem] leading-none tracking-[-0.015em]">
                  Rs. {Number(batch.price_one_time).toLocaleString()}
                </span>
                {showDiscount && (
                  <span className="text-white/40 text-[13px] line-through">
                    Rs. {Number(batch.discount_price_one_time).toLocaleString()}
                  </span>
                )}
                <span className="lms-mono text-white/40 text-[9.5px] uppercase tracking-[0.14em]">one-time</span>
              </>
            ) : (
              <span className="lms-mono text-white/50 text-xs">Contact for pricing</span>
            )}
          </div>
          {batch.price_per_subject && (
            <div className="mt-1.5 lms-mono text-[10.5px] text-white/45 uppercase tracking-[0.06em]">
              or Rs. {Number(batch.price_per_subject).toLocaleString()} <span className="text-white/30 normal-case tracking-normal">per subject</span>
            </div>
          )}
        </div>

        {/* Schedule preview — admin-controlled link surfaces a cinematic
            gold-tinted button that opens the timetable in a new tab. */}
        {batch.schedule_url && (
          <a
            href={batch.schedule_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 group/sched relative inline-flex items-center justify-between gap-3 px-4 py-3 rounded-[12px] overflow-hidden transition-all duration-300 hover:-translate-y-[1px]"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,203,71,0.10) 0%, rgba(255,203,71,0.04) 60%, rgba(12,24,48,0.5) 100%)",
              border: "1px solid rgba(255,203,71,0.32)",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.06) inset, 0 8px 30px -12px rgba(255,203,71,0.25)",
            }}
            data-testid={`batch-schedule-${batch.slug}`}
          >
            {/* glow stripe on hover */}
            <span
              className="absolute inset-y-0 left-0 w-px transition-all duration-500 group-hover/sched:w-full opacity-0 group-hover/sched:opacity-100"
              style={{
                background:
                  "linear-gradient(90deg, rgba(255,203,71,0.12) 0%, rgba(255,203,71,0) 100%)",
              }}
              aria-hidden="true"
            />
            <span className="relative flex items-center gap-2.5">
              <span
                className="w-8 h-8 rounded-[9px] flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,203,71,0.18) 0%, rgba(255,203,71,0.06) 100%)",
                  border: "1px solid rgba(255,203,71,0.4)",
                }}
              >
                <Calendar size={14} className="text-[#ffd970]" />
              </span>
              <span className="flex flex-col leading-tight">
                <span className="lms-mono text-[8.5px] uppercase tracking-[0.18em] text-[#ffd970]/75">
                  Timetable
                </span>
                <span className="text-white text-[13px] font-medium tracking-tight">View schedule</span>
              </span>
            </span>
            <ArrowRight
              size={15}
              className="relative text-[#ffd970]/80 transition-transform duration-300 group-hover/sched:translate-x-0.5"
            />
          </a>
        )}

        {/* CTA row */}
        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={onExplore}
            className="lms-btn lms-btn-ghost h-10 text-xs flex-1 justify-center"
            data-testid={`apply-explore-${batch.slug}`}
          >
            Explore
          </button>
          {isComingSoon ? (
            waLink ? (
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="h-10 text-xs flex-1 rounded-full font-semibold inline-flex items-center justify-center gap-1.5 transition-all bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] text-white hover:shadow-[0_10px_30px_rgba(167,139,250,0.35)] whitespace-nowrap"
                data-testid={`apply-notify-${batch.slug}`}
              >
                Notify me <ArrowRight size={13} />
              </a>
            ) : (
              <button
                type="button"
                disabled
                className="lms-btn lms-btn-ghost h-10 text-xs flex-1 justify-center opacity-60 cursor-not-allowed"
                data-testid={`apply-coming-${batch.slug}`}
              >
                Coming soon
              </button>
            )
          ) : isOpen ? (
            <Link
              to={`/register/${batch.slug}`}
              className="lms-btn lms-btn-primary h-10 text-xs flex-1 justify-center"
              data-testid={`apply-btn-${batch.slug}`}
            >
              Apply now <ArrowRight size={13} />
            </Link>
          ) : (
            <button
              type="button"
              disabled
              className="lms-btn lms-btn-ghost h-10 text-xs flex-1 justify-center opacity-60 cursor-not-allowed"
              data-testid={`apply-closed-${batch.slug}`}
            >
              Not accepting
            </button>
          )}
          {waLink && (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="relative shrink-0 w-10 h-10 rounded-full border border-emerald-400/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400/60 hover:scale-105 transition-all flex items-center justify-center"
              title="Chat on WhatsApp"
              data-testid={`apply-whatsapp-${batch.slug}`}
              aria-label={`Chat about ${batch.name} on WhatsApp`}
            >
              <WhatsAppIcon size={18} />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/* ---------- Explore modal (rich detail view) ---------- */

function ExploreModal({ batch, siteWhatsApp, onClose }) {
  const meta = statusMeta(batch);
  const status = batch.status || (batch.registration_open ? "open" : "closed");
  const isOpen = status === "open" || status === "upcoming";
  const waLink = buildWhatsAppLink(batch.whatsapp_number || siteWhatsApp, batch);
  const featureList = useMemo(() => {
    // Priority: admin-entered highlights (with optional "How it Works" videos)
    // > feature-toggle derivation > canonical LW feature set.
    if (Array.isArray(batch.highlight_items) && batch.highlight_items.length > 0) return batch.highlight_items;
    if (Array.isArray(batch.highlights) && batch.highlights.length > 0) return batch.highlights.map((text) => ({ text, video: null }));
    const f = batch.features || {};
    const derived = [];
    if (f.lectures) derived.push("Concept-first video lectures by top MDCAT instructors");
    if (f.mcqs) derived.push("Advanced MCQ engine & weekly quizzes");
    if (f.notes) derived.push("Printable MDCAT notes for revision");
    if (f.flashcards) derived.push("Spaced-repetition flashcards");
    if (f.mentor) derived.push("1:1 MBBS mentor circle — chat + audio calls");
    if (f.community) derived.push("LW Community — peer learning feed");
    if (f.leaderboard) derived.push("Live leaderboards — subject, chapter & global");
    const texts = derived.length >= 4 ? derived : CANONICAL_LANDING_HIGHLIGHTS;
    return texts.map((text) => ({ text, video: null }));
  }, [batch]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md"
        onClick={onClose}
        data-testid="explore-modal-overlay"
      />
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.97 }}
        transition={{ type: "spring", damping: 28, stiffness: 260 }}
        className="fixed inset-0 z-[85] flex items-center justify-center p-3 md:p-6 pointer-events-none"
      >
        <div
          className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl border border-white/10 bg-[rgba(6,18,38,0.98)] backdrop-blur-2xl pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="explore-modal"
        >
          {/* Hero cover — matches the admin 1600×540 upload guide so the full
              banner stretches edge-to-edge on every viewport, no letterbox. */}
          <div className={`relative ${batchHasCoverVideo(batch) ? "aspect-video" : "aspect-[1600/540]"} overflow-hidden rounded-t-3xl`} style={{
            background: "linear-gradient(135deg, rgba(0,229,255,0.3), rgba(255,211,112,0.18) 60%, rgba(12,24,48,0.9))",
          }}>
            {batch.cover_image || batchHasCoverVideo(batch) ? (
              <BatchCover batch={batch} alt={batch.name} videoFit="cover" />
            ) : (
              <div className="absolute inset-0 cinematic-dots-dense opacity-25" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(6,18,38,0.98)] via-[rgba(6,18,38,0.5)] to-transparent" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur border border-white/15 text-white/80 hover:text-white flex items-center justify-center transition"
              data-testid="explore-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <div className={`absolute top-4 left-4 lms-chip ${meta.cls}`}>{meta.label}</div>
            <div className="absolute bottom-4 left-5 right-5">
              <h2 className="lms-display text-white text-3xl md:text-4xl leading-tight">{batch.name}</h2>
              {batch.tagline && <p className="text-white/75 text-sm mt-2 max-w-xl">{batch.tagline}</p>}
            </div>
          </div>

          {/* Body */}
          <div className="p-6 md:p-8 space-y-6">
            {/* Quick facts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {batch.audience && <Fact label="Audience" value={batch.audience} />}
              {batch.duration && <Fact label="Duration" value={batch.duration} />}
              {batch.language && <Fact label="Language" value={batch.language} />}
              {batch.seat_limit ? <Fact label="Seats" value={`${batch.seat_limit} total`} /> : null}
              {batch.start_date && <Fact label="Starts" value={niceDate(batch.start_date)} />}
              {batch.end_date && <Fact label="Ends" value={niceDate(batch.end_date)} />}
            </div>

            {/* Subjects */}
            {Array.isArray(batch.subjects) && batch.subjects.length > 0 && (
              <div>
                <div className="lms-mono mb-2 text-white/50">— Subjects covered</div>
                <div className="flex flex-wrap gap-2">
                  {batch.subjects.map((s) => (
                    <span
                      key={s}
                      className="px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-xs text-white/80"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Highlights — expandable to avoid overwhelming short attention spans */}
            {featureList.length > 0 && (
              <HighlightsBlock highlights={featureList} />
            )}

            {/* Pricing */}
            {(batch.price_one_time || batch.price_per_subject) && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
                <div className="lms-mono text-white/50 mb-3">— Fees</div>
                <div className="flex flex-wrap gap-6">
                  {batch.price_one_time && (
                    <div>
                      <div className="lms-mono text-white/40 text-[10px]">One-time</div>
                      <div className="flex items-end gap-2 mt-0.5">
                        <span className="lms-display text-white text-2xl">
                          Rs. {Number(batch.price_one_time).toLocaleString()}
                        </span>
                        {batch.discount_price_one_time && batch.discount_price_one_time > batch.price_one_time && (
                          <span className="text-white/40 text-sm line-through">
                            Rs. {Number(batch.discount_price_one_time).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {batch.price_per_subject && (
                    <div>
                      <div className="lms-mono text-white/40 text-[10px]">Per subject</div>
                      <div className="flex items-end gap-2 mt-0.5">
                        <span className="lms-display text-white text-2xl">
                          Rs. {Number(batch.price_per_subject).toLocaleString()}
                        </span>
                        {batch.discount_price_per_subject && batch.discount_price_per_subject > batch.price_per_subject && (
                          <span className="text-white/40 text-sm line-through">
                            Rs. {Number(batch.discount_price_per_subject).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CTAs — mobile: stack + shrink; desktop: side-by-side.
                Apply is the HERO button (2× flex, stronger shadow). WhatsApp is
                a secondary icon-pill so students go through the proper form first. */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              {isOpen ? (
                <Link
                  to={`/register/${batch.slug}`}
                  className="lms-btn lms-btn-primary flex-[2] justify-center h-12 text-sm md:text-base whitespace-nowrap font-semibold shadow-[0_10px_30px_-8px_rgba(92,238,255,0.55)]"
                  data-testid="explore-apply-btn"
                >
                  Open application form <ArrowRight size={16} />
                </Link>
              ) : (
                <button
                  type="button"
                  disabled
                  className="lms-btn lms-btn-ghost flex-[2] justify-center h-12 opacity-60 cursor-not-allowed text-sm"
                >
                  Not accepting right now
                </button>
              )}
              {waLink && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 h-12 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-300/90 hover:bg-emerald-500/20 hover:border-emerald-400/50 transition text-xs whitespace-nowrap sm:flex-none sm:basis-auto sm:max-w-[160px]"
                  title="Prefer WhatsApp? Chat with us."
                  data-testid="explore-whatsapp"
                >
                  <WhatsAppIcon size={14} />
                  <span className="hidden sm:inline">WhatsApp</span>
                  <span className="sm:hidden">Chat on WhatsApp instead</span>
                </a>
              )}
            </div>
            <p className="mt-2 lms-mono text-[10px] text-white/35 hidden sm:block">
              We recommend filling the application form — it's reviewed within 12 hours and unlocks personalised onboarding.
            </p>
          </div>
        </div>
      </motion.div>
    </>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
      <div className="lms-mono text-white/40 text-[10px]">{label}</div>
      <div className="text-white text-sm mt-1">{value}</div>
    </div>
  );
}

function HighlightRow({ item, index, onPlay }) {
  const video = item.video;
  return (
    <li className="flex items-start gap-3 text-white/85 text-sm leading-relaxed" data-testid={`highlight-item-${index}`}>
      <Check size={15} className="mt-0.5 text-[#5ceeff] shrink-0" />
      <span className="flex-1 min-w-0">{item.text}</span>
      {video && (
        <button
          type="button"
          onClick={() => onPlay(item)}
          className="group/hiw shrink-0 inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full border border-[#5ceeff]/25 bg-[#5ceeff]/[0.06] text-[#5ceeff] text-[10.5px] font-medium whitespace-nowrap hover:bg-[#5ceeff]/[0.16] hover:border-[#5ceeff]/55 transition-colors"
          title={`${video.label || "How it Works"} — ${item.text}`}
          data-testid={`highlight-video-btn-${index}`}
        >
          <Play size={9} fill="currentColor" strokeWidth={0} className="transition-transform group-hover/hiw:scale-110" />
          {video.label || "How it Works"}
        </button>
      )}
    </li>
  );
}

function HighlightsBlock({ highlights }) {
  const [playing, setPlaying] = useState(null);
  return (
    <div>
      <div className="lms-mono mb-3 text-white/50">— What's included</div>
      <ul className="space-y-2" data-testid="explore-features-list">
        {highlights.map((h, i) => (
          <HighlightRow key={`${i}-${h.text}`} item={h} index={i} onPlay={setPlaying} />
        ))}
      </ul>
      <AnimatePresence>
        {playing && <FeatureVideoModal item={playing} onClose={() => setPlaying(null)} />}
      </AnimatePresence>
    </div>
  );
}
